import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { BackHeader } from '../components/BackHeader';
import { Shield, Download, FileText, Loader2, Wrench, Upload } from 'lucide-react';
import { certLevelLabel } from '../lib/equipmentCertLevel';
import { CertDayCounter } from '../components/CertDayCounter';
import { certDayTextColor } from '../lib/cert-days';
import { useAuth } from '../contexts/AuthContext';
import { canWriteCertFields } from '../lib/roles';
import { Tooltip } from '../components/ui/tooltip';

export default function EquipmentCertScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // The import writes the same ledger the certification card writes, so it is
  // offered to the same roles and refused server-side to the rest. Hiding it is
  // not the gate; it just keeps a door out of sight that a field hand could not
  // walk through.
  const certWritable = canWriteCertFields(user?.role);

  const { data: certData, isLoading } = useQuery({
    queryKey: ['equipment-cert-summary'],
    queryFn: () => api.equipment.certSummary().then(r => r.data.data ?? r.data ?? []),
  });

  const items: any[] = (certData ?? []).sort((a: any, b: any) => (b.percentUsed ?? 0) - (a.percentUsed ?? 0));

  const handleDownloadPdf = async () => {
    try {
      const res = await api.reports.equipmentCertPdf();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'equipment-cert-usage.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
    }
  };

  const handleDownloadCsv = async () => {
    try {
      const res = await api.reports.equipmentCertCsv();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'equipment-cert-usage.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV download failed:', err);
    }
  };

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
      <BackHeader
        to="/equipment"
        label="Equipment"
        title="Certification Tracking"
        rightAction={
          <div className="flex items-center gap-2">
            {/* The way IN to /equipment/certs/import. A route with no nav entry
                is invisible — Equipment Maintenance shipped routed-but-unlinked
                and was reachable only by typing the URL. */}
            {certWritable && (
              <button
                onClick={() => navigate('/equipment/certs/import')}
                className="flex items-center gap-2 bg-fieldiq-card border border-fieldiq-border text-foreground font-semibold px-3 py-1.5 rounded-lg text-sm hover:bg-secondary/50 transition-colors"
              >
                <Upload size={16} className="text-fieldiq-accent" />
                Import Days
              </button>
            )}
            <Tooltip content="Download PDF">
              <button onClick={handleDownloadPdf} className="p-2 rounded-lg hover:bg-white/5 transition-colors" aria-label="Download PDF">
                <FileText size={18} className="text-fieldiq-accent" />
              </button>
            </Tooltip>
            <Tooltip content="Download CSV">
              <button onClick={handleDownloadCsv} className="p-2 rounded-lg hover:bg-white/5 transition-colors" aria-label="Download CSV">
                <Download size={18} className="text-fieldiq-accent" />
              </button>
            </Tooltip>
          </div>
        }
      />

      <main className="max-w-[1920px] mx-auto px-4 py-4 space-y-4">
        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" />
          </div>
        )}

        {!isLoading && items.length === 0 && (
          <div className="text-center py-12">
            <Shield size={40} className="mx-auto text-fieldiq-muted mb-3" />
            <p className="text-fieldiq-text">No equipment with certification day limits configured.</p>
          </div>
        )}

        {items.map((item: any) => {
          // No `?? 0`. GET /equipment/cert-summary COALESCEs
          // working_days_since_inspection to 0 server-side, so an untracked
          // tool already arrives here as a real-looking zero and no client-side
          // rule can tell it from a freshly inspected one. Reported for the API
          // rather than guessed at here — but this end stops adding zeroes of
          // its own, so the day the endpoint sends null the dash appears.
          const pct = item.percentUsed ?? null;
          const daysUsed = item.daysUsed ?? null;
          const dayLimit = item.certDayLimit ?? null;

          return (
            <div
              key={item.id}
              onClick={() => navigate(`/equipment/${item.id}`)}
              className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4 cursor-pointer hover:border-fieldiq-accent/30 transition-colors"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-fieldiq-accent/15 flex items-center justify-center flex-shrink-0">
                  <Wrench size={18} className="text-fieldiq-accent" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-white">{item.serialNumber || item.name}</h3>
                  {certLevelLabel(item.certDescription, item.certDayLimit) && (
                    <p className="text-sm text-fieldiq-text">
                      {certLevelLabel(item.certDescription, item.certDayLimit)}
                    </p>
                  )}
                </div>
                <span className={`text-sm font-bold ${certDayTextColor(pct)}`}>{pct === null ? '—' : `${pct}%`}</span>
              </div>

              {/* Progress bar.

                  THIS ROW USED TO CARRY BOTH READINGS AT ONCE — "12 / 30
                  working days" on the left and "18 days remaining" on the
                  right, two numbers of opposite meaning in one sentence shape
                  with nothing distinguishing them. It now says days used, like
                  every other bar; the percent above already gives the reader
                  the same information without a second number to misread. */}
              <CertDayCounter
                daysUsed={daysUsed}
                dayLimit={dayLimit}
                mode="used"
                percentUsed={pct}
                size="2xs"
                barHeight="md"
                muted
                showPercent={false}
              />

              {/* Dates */}
              {(item.certStartDate || item.certExpiryDate) && (
                <div className="flex gap-4 mt-2 text-xs text-fieldiq-muted">
                  {item.certStartDate && <span>Start: {item.certStartDate}</span>}
                  {item.certExpiryDate && <span>Expiry: {item.certExpiryDate}</span>}
                </div>
              )}
            </div>
          );
        })}
      </main>
    </div>
  );
}
